import { AsyncSelect } from "components/AsyncInput/AsyncSelect";
import {
  showErrorToast,
  showSuccessToast,
} from "components/CustomAlerts/CustomToast";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { PRIVATE_ROUTE } from "routes/privateRoutes";
import { getNormativas } from "services/normativas";
import { getTitulos } from "services/titulos";
import { AppDispatch, RootState } from "store/store";
import { ModalidadI } from "interfaces/Modalidad";
import { NivelI } from "interfaces/Nivel";
import { OpcionPedagogicaI } from "interfaces/OpcionPedagogica";
import {
  getCombinatoriaById,
  getCombinatoriaUnica,
  postCombinatoria,
  putCombinatoria,
} from "services/combinatoria";
import { createCombinatoriaSchema } from "schemas/combinatoria/CreateCombinatoriaSchema";
import { getModalidades } from "services/modalidad";
import { ModalidadABM } from "screens/Modalidad/ModalidadABM/MoldalidadABM";
import { NivelABM } from "screens/Nivel/NivelABM/NivelABM";
import { OpcionPedagogicaABM } from "screens/OpcionPedagogica/OpcionPedagogicaABM/OpcionPedagogicaABM";
import { getNiveles, getNivelesPaginado } from "services/niveles";
import { getOpcionPedagogica } from "services/opcionPedagogica";

export type abmActionI = "create" | "edit";

interface Props {
  abmAction?: abmActionI;
}

interface combinatoriaABMValuesI {
  id?: string | number;
  modalidad_id: number | string;
  nivel_id: number | string;
  opcion_pedagogica_id: number | string;
}

interface defaultOptionsSelected {
  default_selected_modalidad: ModalidadI | null;
  default_selected_nivel: NivelI | null;
  default_selected_opcion_pedagogica: OpcionPedagogicaI | null;
}

export const CombinatoriaABM = ({ abmAction = "create" }: Props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { alcances, loadingAppData } = useSelector(
    (st: RootState) => st.appData,
  );
  const [modalsActionType, setModalsActionType] = useState<
    "create" | "edit" | undefined
  >();
  const [showModalABM, setShowModalABM] = useState({
    modalidades: false,
    niveles: false,
    opcionesPedagogicas: false,
  });

  const [isLoading, setIsLoading] = useState({
    form: false,
  });

  const [defaultOptionsSelected, setDefaultOptionsSelected] =
    useState<defaultOptionsSelected>({
      default_selected_modalidad: null,
      default_selected_nivel: null,
      default_selected_opcion_pedagogica: null,
    });

  const defaultInitialValues: combinatoriaABMValuesI = {
    modalidad_id: "",
    nivel_id: "",
    opcion_pedagogica_id: "",
  };

  const handleReturn = () => {
    navigate(PRIVATE_ROUTE.COMBINATORIA);
  };

  const handleSubmitCreate = async (params: any) => {
    try {
      const existe = await getCombinatoriaUnica(params);

      if (existe) {
        showErrorToast(
          "La combinatoria no pudo ser creada.",
          "ya existe esa combinatoria",
        );
        return;
      }
      const newCombinatoria = await postCombinatoria({ params });
      handleReturn();
      showSuccessToast("¡La combinación fue creada con éxito!");
    } catch (err: any) {
      showErrorToast("La combinatoria no pudo ser creada.");
      console.log(err.message);
    }
  };

  const handleSubmitEdit = async (params: any) => {
    try {
      const existe = await getCombinatoriaUnica(params);

      if (existe) {
        showErrorToast(
          "La combinatoria no pudo ser creada.",
          "ya existe esa combinatoria",
        );
        return;
      }

      const newCombinatoria = await putCombinatoria({ params });
      handleReturn();
      showSuccessToast("¡La combinatoria fue editada con éxito!");
    } catch (err: any) {
      showErrorToast("La combinatoria no pudo ser editada.");
      console.log(err.message);
    }
  };

  const addNewElemento = ({
    key,
    elemento,
    isMulti,
  }: {
    key: keyof defaultOptionsSelected;
    elemento: any;
    isMulti?: boolean;
  }) => {
    setDefaultOptionsSelected((st: defaultOptionsSelected) => {
      const getPrevMultiSt = () =>
        st[key] && Array.isArray(st[key]) ? (st[key] as any) : [];
      return {
        ...st,
        [key]: isMulti ? [...getPrevMultiSt(), elemento] : elemento,
      };
    });
  };

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    setValues,
  } = useFormik({
    initialValues: defaultInitialValues,
    validationSchema: createCombinatoriaSchema,
    onSubmit: abmAction == "create" ? handleSubmitCreate : handleSubmitEdit,
  });

  const handleCreateModal = (modalName: string) => {
    setModalsActionType("create");
    setShowModalABM((st) => ({ ...st, [modalName]: true }));
  };

  const handleCloseModal = (modalName: string) => {
    setShowModalABM((st) => ({ ...st, [modalName]: false }));
    setTimeout(() => setModalsActionType(undefined), 500);
  };

  const loadEditForm = async () => {
    const errorLoadingNotificacionResolve = () => {
      showErrorToast("No fue posible cargar la combinatoria a editar");
      navigate(PRIVATE_ROUTE.COMBINATORIA_EDITAR);
    };

    try {
      setIsLoading((st) => ({ ...st, form: true }));

      if (!id) return errorLoadingNotificacionResolve();

      const combinatoria = await getCombinatoriaById(id);

      if (!combinatoria) return errorLoadingNotificacionResolve();

      const initialValues = {
        id: combinatoria.id,
        modalidad_id: combinatoria.modalidad.id,
        nivel_id: combinatoria.nivel.id,
        opcion_pedagogica_id: combinatoria.opcion_pedagogica.id,
      };

      setDefaultOptionsSelected({
        default_selected_modalidad: combinatoria.modalidad,
        default_selected_nivel: combinatoria.nivel,
        default_selected_opcion_pedagogica: combinatoria.opcion_pedagogica,
      });
      setValues(initialValues);
    } catch (err) {
      errorLoadingNotificacionResolve();
    } finally {
      setIsLoading((st) => ({ ...st, form: false }));
    }
  };

  const addNewModalidad = (elemento: ModalidadI) => {
    addNewElemento({
      key: "default_selected_modalidad",
      elemento,
      isMulti: false,
    });
    setValues({ ...values, modalidad_id: elemento.id });
  };

  const addNewNivel = (elemento: NivelI) => {
    addNewElemento({ key: "default_selected_nivel", elemento, isMulti: false });
    setValues({ ...values, nivel_id: elemento.id });
  };

  const addNewOpcionPedagogica = (elemento: OpcionPedagogicaI) => {
    addNewElemento({
      key: "default_selected_opcion_pedagogica",
      elemento,
      isMulti: false,
    });
    setValues({ ...values, opcion_pedagogica_id: elemento.id });
  };

  useEffect(() => {
    if (abmAction == "edit") loadEditForm();
  }, []);

  return (
    <section>
      <h1 className="section-title">
        {abmAction == "edit" ? "Editar combinatoria" : "Nueva combinatoria"}
      </h1>

      <h2 className="section-subtitle mt-2">
        Edita o crea una nueva combinatoria.
      </h2>
      <Card>
        <form onSubmit={handleSubmit}>
          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="modalidad_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de modalidad"}
                size="lg"
                title="Modalidad"
                getter={getModalidades}
                paramKey={"nombre"}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: ModalidadI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_modalidad"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("modalidades")}
                variant="secondary"
                size={"lg"}
                style={{
                  width: "175px",
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nueva Modalidad
              </Button>
            </Col>
          </Row>
          <hr></hr>
          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="nivel_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre del nivel"}
                size="lg"
                title="Nivel"
                getter={getNiveles}
                paramKey={"nombre"}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: NivelI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_nivel"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("niveles")}
                variant="secondary"
                size={"lg"}
                style={{
                  width: "175px",
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nuevo Nivel
              </Button>
            </Col>
          </Row>
          <hr></hr>
          <Row className="">
            <Col sm={8} className="mb-3">
              <AsyncSelect
                onChange={handleChange}
                id="opcion_pedagogica_id"
                values={values}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                placeholder={"Buscar nombre de la opción pedagógica"}
                size="lg"
                title="Opción Pedagógica"
                getter={getOpcionPedagogica}
                paramKey={"nombre"}
                variant="gray-container"
                arrElementsPath="elementos"
                optionsFormatConfig={{
                  value: "id",
                  label: (el: OpcionPedagogicaI) => `${el.nombre}`,
                }}
                defaultSelectedOptions={
                  defaultOptionsSelected["default_selected_opcion_pedagogica"]
                }
                required
                noOptionsMessage={`No hay opciones disponibles para el nombre ingresado`}
                emptySelectMessage={`Ingresar un nombre`}
              />
            </Col>
            <Col>
              <Button
                onClick={() => handleCreateModal("opcionesPedagogicas")}
                variant="secondary"
                size={"lg"}
                style={{
                  width: "175px",
                  height: "50px",
                  marginTop: "20px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                }}
              >
                Nueva Opción Pedagógica
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="w-100 d-flex column-gap-3 justify-content-end">
                <Button variant="secondary" size={"lg"} onClick={handleReturn}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Confirmando..." : "Confirmar"}
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </Card>

      <ModalidadABM
        showModal={showModalABM.modalidades}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("modalidades")}
        onSuccess={(newEl: ModalidadI) => addNewModalidad(newEl)}
      />
      <NivelABM
        showModal={showModalABM.niveles}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("niveles")}
        onSuccess={(newEl: NivelI) => addNewNivel(newEl)}
      />
      <OpcionPedagogicaABM
        showModal={showModalABM.opcionesPedagogicas}
        actionType={modalsActionType}
        onClose={() => handleCloseModal("opcionesPedagogicas")}
        onSuccess={(newEl: OpcionPedagogicaI) => addNewOpcionPedagogica(newEl)}
      />
    </section>
  );
};
